import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { IGlobalSetting } from '@models/settings/global-setting';
import { StoreActions } from '@models/store/store-actions';
import { IStoreState } from '@models/store/store-state';
import { ObservableStore } from '@services/observable-store/dist';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, retry, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class GlobalSettingsService extends ObservableStore<IStoreState> {
  private settingsApiURL: string;

  public settings$: BehaviorSubject<IGlobalSetting[]> = new BehaviorSubject([]);

  constructor(private readonly http: HttpClient, private readonly appConfig: AppConfigProvider) {
    super({ trackStateHistory: false });
    this.appConfig.environment.subscribe(env => {
      this.settingsApiURL = `${env.API.SETTINGS}/api/settings`;
    });
  }

  public get culture() {
    return 'en-US';
  }

  public getSettings(forceFetch = false): Observable<IGlobalSetting[]> {
    const state = this.getState();
    if (state && state.globalSettings && !forceFetch) {
      return of(state.globalSettings);
    } else {
      return this.fetchSettings();
    }
  }

  private fetchSettings(): Observable<IGlobalSetting[]> {
    return this.http.get<IGlobalSetting[]>(this.settingsApiURL).pipe(
      tap(settings => {
        this.setState({ globalSettings: settings }, StoreActions.GetGlobalSettings);
        this.settings$.next(settings);
        return settings;
      }),
      retry(3),
      catchError(this.handleError)
    );
  }

  public getSettingByName$(settingName: string): Observable<IGlobalSetting> {
    const state = this.getState();
    if (state && state.globalSettings) {
      const setting = state.globalSettings.find(({ SettingName }) => SettingName === settingName);
      return of(setting);
    } else {
      return this.getSettings().pipe(
        mergeMap(settings => settings),
        filter(setting => setting.SettingName === settingName)
      );
    }
  }

  public getSettingByName(settingName: string, forceFetch = false): Promise<IGlobalSetting> {
    const state = this.getState();
    if (state && state.globalSettings && !forceFetch) {
      const setting = state.globalSettings.find(({ SettingName }) => SettingName === settingName);
      return Promise.resolve(setting);
    } else {
      return this.getSettings(forceFetch)
        .toPromise()
        .then(() => this.getSettingByName(settingName));
    }
  }

  private handleError(error: any) {
    console.error('server error:', error);
    if (error.error instanceof Error) {
      const errMessage = error.error.message;
      return throwError(errMessage);
    }
    return throwError(error || 'Server error');
  }

  public updateGlobalSettings(settings: IGlobalSetting[]) {
    return this.http.post<{ updated_rows: IGlobalSetting[] }>(this.settingsApiURL, settings).pipe(
      map(savedSettings => {
        this.setState({ globalSettings: savedSettings.updated_rows }, StoreActions.GetGlobalSettings);
        this.settings$.next(savedSettings.updated_rows);
        return savedSettings;
      })
    );
  }
}
